/**
 * Menu Permission Helper
 * Centralized permission checking for side menu items based on merchant permissions
 */

/**
 * Check if a menu item should be visible based on merchant permissions
 * @param {Object} menuItem - The menu item to check
 * @param {Object} context - Context object with permission checking methods
 * @returns {boolean} - True if menu should be visible
 */
export const shouldShowMenu = (menuItem, context) => {
  // If no permission rules defined, show by default
  if (!menuItem.permissions) {
    return true;
  }

  const {
    isBBShopMerchant,
    shopHasAgents,
    isBdShop,
    isCustomPageEnabled,
    isVideoEnabled,
    isCourseEnabled,
    isThirdPartyShopHasWeapp,
    isBBLbsShop,
    isArticleEnabled,
    isProductEnabled,
    isMultiUnitEnabled,
    isWholesaleStoreVerificationEnabled,
    isLotteryEnabled,
    isLotteryQrcodeEnabled,
    isMembersEnabled,
    isLbsShop,
    isSaasLbsShop,
    isSaasShop,
    isShopsEnabled,
    isSoleLotteryWeapp,
    isSuperAdmin,
    isMerchantCuratedPoiMode,
    isGrouponMerchant,
    isBalanceVipEnabled,
    isNewWechatTransferEnabled,
    isSpecialShareEnabled,
    isTaskCenterEnabled,
  } = context;

  const { permissions } = menuItem;

  if (permissions.hideForGrouponMerchant && isGrouponMerchant()) {
    return false;
  }

  if (permissions.onlyForGrouponMerchant && !isGrouponMerchant()) {
    return false;
  }

  // Check if menu requires BB Shop Merchant
  if (permissions.requiresBBShopMerchant && !isBBShopMerchant()) {
    return false;
  }

  // Check if menu requires shop to have agents
  if (permissions.requiresAgents && !shopHasAgents()) {
    return false;
  }

  if (
    permissions.requiresAgentsOrSpecialShare &&
    !shopHasAgents() &&
    !isSpecialShareEnabled()
  ) {
    return false;
  }

  // Check if menu requires BB Shop Merchant OR shop has agents
  if (permissions.requiresBBShopOrAgents) {
    if (!isBBShopMerchant() && !shopHasAgents()) {
      return false;
    }
  }

  // Check if menu should be hidden for BD shops
  if (permissions.hideForBdShop && isBdShop()) {
    return false;
  }

  // Check if menu requires course feature
  if (permissions.requiresCourse && !isCourseEnabled()) {
    return false;
  }

  // Check if menu requires weapp (BB Shop OR third party with weapp)
  if (permissions.requiresWeapp) {
    if (!isBBShopMerchant() && !isThirdPartyShopHasWeapp()) {
      return false;
    }
  }

  if (permissions.requiresWholesaleStoreVerification) {
    if (
      !isWholesaleStoreVerificationEnabled ||
      !isWholesaleStoreVerificationEnabled()
    ) {
      return false;
    }
  }

  if (permissions.requiresWeappOrWholesaleStoreVerification) {
    const hasWeapp = isBBShopMerchant() || isThirdPartyShopHasWeapp();
    const hasWholesale =
      isWholesaleStoreVerificationEnabled &&
      isWholesaleStoreVerificationEnabled();
    if (!hasWeapp && !hasWholesale) {
      return false;
    }
  }

  // Check if menu requires BB LBS Shop
  if (permissions.requiresBBLbsShop && !isBBLbsShop()) {
    return false;
  }

  // Check if menu requires article feature
  if (permissions.requiresArticle) {
    if (!isBBShopMerchant() && !isArticleEnabled()) {
      return false;
    }
  }

  if(permissions.requiresProduct && !isProductEnabled()) {
    return false;
  }

  if (permissions.requiresMultiUnit && (!isMultiUnitEnabled || !isMultiUnitEnabled())) {
    return false;
  }

  // Check if menu requires lottery feature
  if (permissions.requiresLottery) {
    if (!isBBShopMerchant() && !isLotteryEnabled()) {
      return false;
    }
  }

  // Check if menu requires lottery qrcode feature
  if (permissions.requiresLotteryQrcode && !isLotteryQrcodeEnabled()) {
    return false;
  }

  // Check if menu requires members feature
  if (permissions.requiresMembers) {
    if (!isBBShopMerchant() && !isMembersEnabled()) {
      return false;
    }
  }

  // Check if menu requires LBS shop
  if (permissions.requiresLbsShop && !isLbsShop()) {
    return false;
  }

  if (permissions.requiresLbsShopOrWholesale) {
    const hasLbs = isLbsShop && isLbsShop();
    const hasWholesale =
      isWholesaleStoreVerificationEnabled &&
      isWholesaleStoreVerificationEnabled();
    if (!hasLbs && !hasWholesale) {
      return false;
    }
  }

  if (permissions.requiresSaasLbsShop && !(isSaasLbsShop && isSaasLbsShop())) {
    return false;
  }

  if (permissions.requiresSaasLbsShopOrWholesale) {
    const hasLbs = isSaasLbsShop && isSaasLbsShop();
    const hasWholesale =
      isWholesaleStoreVerificationEnabled &&
      isWholesaleStoreVerificationEnabled();
    if (!hasLbs && !hasWholesale) {
      return false;
    }
  }

  if (permissions.requiresSaasShop && !(isSaasShop && isSaasShop())) {
    return false;
  }

  if (permissions.requiresMerchantCuratedPoi && !isMerchantCuratedPoiMode()) {
    return false;
  }
  if (permissions.requiresShops && !isShopsEnabled()) {
    return false;
  }

  // Check if menu should only show for sole lottery weapp
  if (permissions.onlyForSoleLotteryWeapp && !isSoleLotteryWeapp()) {
    return false;
  }

  // Check if menu should hide for sole lottery weapp
  if (permissions.hideForSoleLotteryWeapp && isSoleLotteryWeapp()) {
    return false;
  }

  // Check if menu requires third party shop with weapp OR no agents
  if (permissions.requiresWeappOrNoAgents) {
    if (!isThirdPartyShopHasWeapp() && shopHasAgents()) {
      return false;
    }
  }

  // Check if menu should hide for BB LBS shops
  if (permissions.hideForBBLbsShop && isBBLbsShop()) {
    return false;
  }

  // Check if menu should only show for BD shops
  if (permissions.onlyForBdShop && !isBdShop()) {
    return false;
  }

  if (permissions.requiresCustomPage && !isCustomPageEnabled()) {
    return false;
  }
  if (permissions.requiresVideo && !isVideoEnabled()) {
    return false;
  }

  // Admin section: only super admin can see and visit
  if (permissions.requiresSuperAdmin && !isSuperAdmin()) {
    return false;
  }

  // Check if menu requires balance VIP topup feature
  if (permissions.requiresBalanceVip && !isBalanceVipEnabled()) {
    return false;
  }

  // Check if menu requires new WeChat transfer (other.enabled_transfer_new)
  if (permissions.requiresNewWechatTransfer && !isNewWechatTransferEnabled()) {
    return false;
  }

  if (permissions.requiresTaskCenter && !isTaskCenterEnabled()) {
    return false;
  }

  return true;
};

/**
 * Filter menu items and their children based on permissions
 * @param {Array} menus - Array of menu items
 * @param {Object} context - Context object with permission checking methods
 * @returns {Array} - Filtered menu items
 */
export const filterMenusByPermissions = (menus, context) => {
  return menus
    .map((menu) => {
      // Check if parent menu should be shown
      if (!shouldShowMenu(menu, context)) {
        return null;
      }

      // Filter children if they exist
      if (menu.children && menu.children.length > 0) {
        const filteredChildren = menu.children
          .map((child) => {
            if (!shouldShowMenu(child, context)) {
              return null;
            }
            return child;
          })
          .filter((child) => child !== null);

        // If parent has children but all are filtered out, hide parent too
        if (filteredChildren.length === 0 && menu.children.length > 0) {
          return null;
        }

        return {
          ...menu,
          children: filteredChildren,
        };
      }

      return menu;
    })
    .filter((menu) => menu !== null);
};
